import { inlineCodexReference, prepareCodexReference } from './codexText';
import { chatGPTBackendModel, type ChatGPTImageModel } from './chatgptImageModels';
import { generateWithQwen21 } from './qwenImageGeneration';
import { QWEN21_MODEL_ID } from './qwenImageModels';
/**
 * AI Image Generation Service
 * Modular architecture - easily add new models
 */

import { getModelConfig, DEFAULT_IMAGE_MODEL } from './model-config';
import { supabase } from './supabase';
import { resolveReferenceImageUrl } from './referenceImageUrl';
import { optimizePrompt, getNegativePrompt } from './prompt-utils';
import { prepareVRAMForTask } from './comfyui-vram';
import { saveGeneratedImageToR2 } from './character2dService';

// ============================================
// LOCAL INFERENCE TOGGLE (Development Only)
// ============================================
// Set VITE_USE_LOCAL_INFERENCE=true in .env.local to use local ComfyUI
// instead of Replicate API. This saves money during development.
// To switch back: just remove/comment the env variable. No code changes needed!
const USE_LOCAL_INFERENCE = import.meta.env.VITE_USE_LOCAL_INFERENCE === 'true';
const LOCAL_COMFYUI_URL = import.meta.env.VITE_LOCAL_COMFYUI_URL || 'http://127.0.0.1:8188';

// Debug: Always log the env var state at module load
console.log('🔧 LOCAL INFERENCE ENV CHECK:', {
    raw: import.meta.env.VITE_USE_LOCAL_INFERENCE,
    parsed: USE_LOCAL_INFERENCE,
    url: LOCAL_COMFYUI_URL
});

if (USE_LOCAL_INFERENCE) {
    console.log('🏠 LOCAL INFERENCE MODE ENABLED - Using ComfyUI at', LOCAL_COMFYUI_URL);
} else {
    console.log('☁️ Using Replicate API (VITE_USE_LOCAL_INFERENCE is not "true")');
}

// ============================================
// TYPES
// ============================================

export type ModelId =
    | typeof QWEN21_MODEL_ID
    | `openai/${ChatGPTImageModel}`
    | 'openai/chatgpt-image'           // ChatGPT via private server Codex OAuth
    | 'google/nano-banana'            // Google Nano Banana (Free tier)
    | 'google/nano-banana-pro'        // Google Nano Banana Pro
    | 'tongyi-mai/z-image'            // Z-Image Foundation (CFG 4.0, 50 steps, negative prompts)
    | 'prunaai/z-image-turbo'         // Z-Image Turbo (txt2img)
    | 'prunaai/z-image-turbo-img2img' // Z-Image Turbo (img2img)
    | 'prunaai/z-image-turbo-inpaint' // Z-Image Turbo (inpaint)
    | 'prunaai/z-image-turbo-controlnet' // Z-Image Turbo (ControlNet)
    | 'prunaai/z-image-turbo-lora'    // Z-Image Turbo (LoRA)
    | 'nunchaku/z-image-doodle'       // Nunchaku 4-bit Z-Image (fastest for Doodle)
    | 'cagliostrolab/animagine-xl-3.1' // Animagine XL 3.1 (Anime)
    | 'models/gemini-3-pro-image-preview'; // Google Gemini 3 Pro (Preview)

export interface GenerationRequest {
    prompt: string;
    model?: ModelId;
    aspectRatio?: '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '21:9' | '9:21' | '3:2' | '2:3' | '4:5' | '5:4';
    width?: number;
    height?: number;
    seed?: number;
    inputImage?: string;
    referenceImages?: string[];
    maskImage?: string;
    editMode?: 'inpainting' | 'outpainting';
    strength?: number; // Img2Img strength (0-1): lower = more preservation of input
    controlnetConditioningScale?: number; // ControlNet conditioning scale (0-2, default 0.75)
    numInferenceSteps?: number; // Number of inference steps (1-9 for progressive drawing)
    outputSize?: '1K' | '2K' | '4K'; // Output resolution for Google models (default: 2K)
    resolution?: '1080p' | '2K' | '4K' | 'Low' | 'Medium' | 'High' | 'Fast' | 'High Quality' | 'Best Quality' | 'Extra High' | 'Max';
    quality?: 'HQ' | 'HD'; // HQ = FP8 model (fast, 16GB VRAM), HD = BF16 model (higher quality)
    poseMode?: boolean; // Extract pose from input image using DWPose
    selections?: Record<string, any>;
    characterReference?: {
        name: string;
        snapshotUrl: string;
        snapshotUrls?: string[]; // Multiple reference images for better identity consistency
        metadata: any;
    };
    userId?: string; // For credit deduction in edge function
}

export interface GenerationResult {
    imageBlob?: Blob;
    imageUrl?: string;
    model: string;
    prompt: string;
}

// Helper to get dimensions for Z-Image-Turbo
// Uses exact recommended resolutions from Z-Image documentation
// HQ = 1024 base, HD = 1280 base, UHD = 1536 base
// Exported for use in canvas placement
export function getDimensionsFor2MP(aspectRatio: string = '9:16', resolution: string = 'HQ'): { width: number, height: number } {
    // Exact recommended dimensions for each resolution tier
    const dimensions: Record<string, Record<string, { width: number, height: number }>> = {
        'HQ': { // 1024 Resolution
            '1:1': { width: 1024, height: 1024 },
            '9:7': { width: 1152, height: 896 },
            '7:9': { width: 896, height: 1152 },
            '4:3': { width: 1152, height: 864 },
            '3:4': { width: 864, height: 1152 },
            '3:2': { width: 1248, height: 832 },
            '2:3': { width: 832, height: 1248 },
            '16:9': { width: 1280, height: 720 },
            '9:16': { width: 720, height: 1280 },
            '21:9': { width: 1344, height: 576 },
            '9:21': { width: 576, height: 1344 },
        },
        'HD': { // 1280 Resolution
            '1:1': { width: 1280, height: 1280 },
            '9:7': { width: 1440, height: 1120 },
            '7:9': { width: 1120, height: 1440 },
            '4:3': { width: 1472, height: 1104 },
            '3:4': { width: 1104, height: 1472 },
            '3:2': { width: 1536, height: 1024 },
            '2:3': { width: 1024, height: 1536 },
            '16:9': { width: 1536, height: 864 },
            '9:16': { width: 864, height: 1536 },
            '21:9': { width: 1680, height: 720 },
            '9:21': { width: 720, height: 1680 },
        },
        'UHD': { // 1536 Resolution
            '1:1': { width: 1536, height: 1536 },
            '9:7': { width: 1728, height: 1344 },
            '7:9': { width: 1344, height: 1728 },
            '4:3': { width: 1728, height: 1296 },
            '3:4': { width: 1296, height: 1728 },
            '3:2': { width: 1872, height: 1248 },
            '2:3': { width: 1248, height: 1872 },
            '16:9': { width: 2048, height: 1152 },
            '9:16': { width: 1152, height: 2048 },
            '21:9': { width: 2016, height: 864 },
            '9:21': { width: 864, height: 2016 },
        },
    };

    // Map UI labels to internal resolution keys
    // Fast → HQ (1024), High Quality → HD (1280), Best Quality → UHD (1536)
    const labelToKey: Record<string, string> = {
        'Fast': 'HQ',
        'High Quality': 'HD',
        'Best Quality': 'UHD',
        'HQ': 'HQ',
        'HD': 'HD',
        'UHD': 'UHD',
        '2K': 'UHD',
    };
    const resolutionKey = labelToKey[resolution || 'High Quality'] || 'HD';
    const resDimensions = dimensions[resolutionKey] || dimensions['HD'];

    // Return exact dimensions or fallback to 9:16 default
    return resDimensions[aspectRatio] || resDimensions['9:16'] || { width: 720, height: 1280 };
}

// ============================================
// LOCAL COMFYUI INFERENCE (Development Only)
// ============================================
// This function routes requests to your local ComfyUI when USE_LOCAL_INFERENCE is true
async function generateWithLocalComfyUI(request: GenerationRequest): Promise<GenerationResult> {
    console.log('🏠 Generating with LOCAL ComfyUI...', request.model);

    // Use explicit dimensions if provided, otherwise calculate from aspect ratio
    const calculatedDims = getDimensionsFor2MP(request.aspectRatio, request.resolution);
    const width = request.width || calculatedDims.width;
    const height = request.height || calculatedDims.height;

    // Determine workflow type based on model/request
    let workflowType = 'txt2img';

    // Check for Nunchaku 4-bit ControlNet (fastest for Doodle page)
    const useNunchaku = request.model?.includes('nunchaku') || request.model?.includes('doodle');

    if (request.model?.includes('controlnet') || request.controlnetConditioningScale) {
        workflowType = useNunchaku ? 'nunchaku-controlnet' : 'controlnet';
    } else if (request.inputImage && request.maskImage) {
        workflowType = 'inpaint';
    } else if (request.inputImage) {
        workflowType = 'img2img';
    }

    // Select model based on quality setting
    // HQ = FP8 model (faster, fits 16GB VRAM with high res)
    // HD = BF16 model (higher quality, needs more VRAM)
    const diffusionModelName = request.quality === 'HD'
        ? 'z_image_turbo_bf16.safetensors'
        : 'zImageFp8_turbo.safetensors';

    console.log(`🎨 Using local workflow: ${workflowType}, dimensions: ${width}x${height}, model: ${diffusionModelName}`);

    let comfyPayload: any;

    // ========== ANIMAGINE WORKFLOW (SDXL-based) ==========
    // Animagine uses CheckpointLoaderSimple (model+clip+vae in one)
    // Different from Z-Image-Turbo which is a DiT model
    const isAnimagine = request.model?.startsWith('cagliostrolab/animagine');
    if (isAnimagine) {
        console.log('🎌 Using Animagine XL workflow (SDXL-based)');

        // Animagine recommended settings: CFG 5, Steps 28, euler_ancestral
        // Get model config for prompt optimization
        const config = getModelConfig(request.model!);
        const negativePrompt = config?.optimization?.negativePrompt ||
            'lowres, bad anatomy, bad hands, text, error, missing finger, extra digits, fewer digits, cropped, worst quality, low quality, low score, bad score, average score, signature, watermark, username, blurry';

        // Apply suffix optimization (quality tags at end for Animagine v4.0)
        let optimizedPrompt = request.prompt;
        if (config?.optimization?.suffix) {
            optimizedPrompt = request.prompt + config.optimization.suffix;
        }

        // Animagine official recommended dimensions
        const animagineDims: Record<string, { width: number, height: number }> = {
            '1:1': { width: 1024, height: 1024 },   // Square
            '9:7': { width: 1152, height: 896 },    // Landscape
            '3:2': { width: 1216, height: 832 },    // Landscape
            '7:4': { width: 1344, height: 768 },    // Landscape
            '12:5': { width: 1536, height: 640 },   // Landscape (wide)
            '7:9': { width: 896, height: 1152 },    // Portrait
            '2:3': { width: 832, height: 1216 },    // Portrait
            '4:7': { width: 768, height: 1344 },    // Portrait
            '5:12': { width: 640, height: 1536 },   // Portrait (tall)
        };
        const animDims = animagineDims[request.aspectRatio || '2:3'] || { width: 832, height: 1216 };

        if (workflowType === 'txt2img') {
            comfyPayload = {
                prompt: {
                    "1": {
                        inputs: { ckpt_name: "animagine-xl-4.0.safetensors" },
                        class_type: "CheckpointLoaderSimple"
                    },
                    "2": {
                        inputs: { text: optimizedPrompt, clip: ["1", 1] },
                        class_type: "CLIPTextEncode"
                    },
                    "3": {
                        inputs: { text: negativePrompt, clip: ["1", 1] },
                        class_type: "CLIPTextEncode"
                    },
                    "4": {
                        inputs: { width: animDims.width, height: animDims.height, batch_size: 1 },
                        class_type: "EmptyLatentImage"
                    },
                    "5": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: 50,
                            cfg: 5,
                            sampler_name: "euler_ancestral",
                            scheduler: "normal",
                            denoise: 1,
                            model: ["1", 0],
                            positive: ["2", 0],
                            negative: ["3", 0],
                            latent_image: ["4", 0]
                        },
                        class_type: "KSampler"
                    },
                    "6": {
                        inputs: { samples: ["5", 0], vae: ["1", 2] },
                        class_type: "VAEDecode"
                    },
                    "7": {
                        inputs: { filename_prefix: "ezart_animagine", images: ["6", 0] },
                        class_type: "SaveImage"
                    }
                }
            };
        } else {
            throw new Error(`Animagine only supports txt2img workflow`);
        }
    } else if (request.model?.startsWith('tongyi-mai/z-image')) {
        // ========== Z-IMAGE FOUNDATION WORKFLOW (DiT-based, full quality) ==========
        // Z-Image Foundation: CFG 4.0, 50 steps, supports negative prompts
        // Higher quality, more diversity than Turbo but slower
        console.log('🎨 Using Z-Image Foundation workflow (50 steps, CFG 4.0)');

        const config = getModelConfig(request.model!);
        const negativePrompt = config?.optimization?.negativePrompt ||
            'low quality, blurry, distorted, artifacts, watermark, text, signature, deformed';

        if (workflowType === 'txt2img') {
            // Z-Image Foundation with ModelSamplingAuraFlow shift: 3
            comfyPayload = {
                prompt: {
                    "1": {
                        inputs: { unet_name: "z_image_bf16.safetensors", weight_dtype: "default" },
                        class_type: "UNETLoader"
                    },
                    "2": {
                        inputs: { clip_name: "qwen_3_4b_fp8_mixed.safetensors", type: "lumina2", device: "default" },
                        class_type: "CLIPLoader"
                    },
                    "3": {
                        inputs: { vae_name: "ae.safetensors" },
                        class_type: "VAELoader"
                    },
                    "4": {
                        inputs: { shift: 3, model: ["1", 0] },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    "5": {
                        inputs: { text: request.prompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "6": {
                        inputs: { text: negativePrompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "7": {
                        inputs: { width, height, batch_size: 1 },
                        class_type: "EmptySD3LatentImage"
                    },
                    "8": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: 50,
                            cfg: 4.0,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: 1,
                            model: ["4", 0],
                            positive: ["5", 0],
                            negative: ["6", 0],
                            latent_image: ["7", 0]
                        },
                        class_type: "KSampler"
                    },
                    "9": {
                        inputs: { samples: ["8", 0], vae: ["3", 0] },
                        class_type: "VAEDecode"
                    },
                    "10": {
                        inputs: { filename_prefix: "ezart_zimage", images: ["9", 0] },
                        class_type: "SaveImage"
                    }
                }
            };
        } else if (workflowType === 'img2img' && request.inputImage) {
            const imageName = await uploadImageToComfyUI(request.inputImage);
            const denoise = request.strength ?? 0.7;

            comfyPayload = {
                prompt: {
                    "1": {
                        inputs: { unet_name: "z_image_bf16.safetensors", weight_dtype: "default" },
                        class_type: "UNETLoader"
                    },
                    "2": {
                        inputs: { clip_name: "qwen_3_4b_fp8_mixed.safetensors", type: "lumina2", device: "default" },
                        class_type: "CLIPLoader"
                    },
                    "3": {
                        inputs: { vae_name: "ae.safetensors" },
                        class_type: "VAELoader"
                    },
                    "4": {
                        inputs: { shift: 3, model: ["1", 0] },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    "5": {
                        inputs: { text: request.prompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "6": {
                        inputs: { text: negativePrompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "7": {
                        inputs: { image: imageName, upload: "image" },
                        class_type: "LoadImage"
                    },
                    "8": {
                        inputs: { pixels: ["7", 0], vae: ["3", 0] },
                        class_type: "VAEEncode"
                    },
                    "9": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: 50,
                            cfg: 4.0,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: denoise,
                            model: ["4", 0],
                            positive: ["5", 0],
                            negative: ["6", 0],
                            latent_image: ["8", 0]
                        },
                        class_type: "KSampler"
                    },
                    "10": {
                        inputs: { samples: ["9", 0], vae: ["3", 0] },
                        class_type: "VAEDecode"
                    },
                    "11": {
                        inputs: { filename_prefix: "ezart_zimage_i2i", images: ["10", 0] },
                        class_type: "SaveImage"
                    }
                }
            };
        } else {
            throw new Error(`Z-Image Foundation only supports txt2img and img2img workflows`);
        }
    } else {
        // ========== Z-IMAGE-TURBO WORKFLOW (DiT-based) ==========
        // Build the ComfyUI API request for Z-Image-Turbo
        // Z-Image-Turbo is a DiT model that requires:
        // - Separate diffusion model loader (UNETLoader or DiffusionLoader)
        // - Qwen text encoder (for T5/Qwen-based text encoding)
        // - Separate VAE loader
        // - For ControlNet: Z-Image-Turbo-Fun-Controlnet-Union model patch

        if (workflowType === 'controlnet' && request.inputImage) {
            // ControlNet workflow for Z-Image-Turbo using VideoX-Fun nodes
            // Based on the working Z-Image-Turbo Fun Control to Image workflow
            const imageName = await uploadImageToComfyUI(request.inputImage);
            const controlStrength = request.controlnetConditioningScale || 1.0;
            const steps = request.numInferenceSteps || 9; // 9 steps for better quality with FP8 speed
            const usePoseMode = request.poseMode === true;

            console.log('🎮 ControlNet mode:', usePoseMode ? 'POSE EXTRACTION (DWPose)' : 'DIRECT SKETCH');

            // Build base workflow nodes
            const baseNodes: Record<string, any> = {
                // 1. Load diffusion model (UNETLoader) - FP8 for faster real-time inference
                "1": {
                    inputs: {
                        unet_name: diffusionModelName,
                        weight_dtype: "default"
                    },
                    class_type: "UNETLoader"
                },
                // 2. Load CLIP/Text Encoder (Qwen)
                "2": {
                    inputs: {
                        clip_name: "qwen_3_4b.safetensors",
                        type: "lumina2",
                        device: "default"
                    },
                    class_type: "CLIPLoader"
                },
                // 3. Load VAE
                "3": {
                    inputs: {
                        vae_name: "ae.safetensors"
                    },
                    class_type: "VAELoader"
                },
                // 4. Load ControlNet model patch (8-step optimized)
                "4": {
                    inputs: {
                        name: "Z-Image-Turbo-Fun-Controlnet-Union-2.1-2601-8steps.safetensors"
                    },
                    class_type: "ModelPatchLoader"
                },
                // 5. Load control image (user's sketch or camera photo)
                "5": {
                    inputs: {
                        image: imageName,
                        upload: "image"
                    },
                    class_type: "LoadImage"
                },
                // 6. Scale image to max dimension
                "6": {
                    inputs: {
                        image: ["5", 0],
                        upscale_method: "lanczos",
                        largest_size: Math.max(width, height)
                    },
                    class_type: "ImageScaleToMaxDimension"
                },
            };

            // Add DWPose node if in pose mode (extracts skeleton from camera photo)
            if (usePoseMode) {
                baseNodes["17"] = {
                    inputs: {
                        image: ["6", 0],  // Input from scaled image
                        detect_hand: "enable",
                        detect_body: "enable",
                        detect_face: "enable",
                        resolution: 1024,
                        bbox_detector: "yolox_l.onnx",
                        pose_estimator: "dw-ll_ucoco_384_bs5.torchscript.pt"
                    },
                    class_type: "DWPreprocessor"
                };
            }

            // Control image source: either direct sketch (node 6) or pose skeleton (node 17)
            const controlImageSource = usePoseMode ? ["17", 0] : ["6", 0];

            // Continue building the workflow
            comfyPayload = {
                prompt: {
                    ...baseNodes,
                    // 7. Get image dimensions for latent
                    "7": {
                        inputs: {
                            image: ["6", 0]  // Always use original scaled image for dimensions
                        },
                        class_type: "GetImageSize"
                    },
                    // 8. Create empty latent at control image size
                    "8": {
                        inputs: {
                            width: ["7", 0],
                            height: ["7", 1],
                            batch_size: 1
                        },
                        class_type: "EmptySD3LatentImage"
                    },
                    // 9. Encode positive prompt
                    "9": {
                        inputs: {
                            text: request.prompt,
                            clip: ["2", 0]
                        },
                        class_type: "CLIPTextEncode"
                    },
                    // 10. Create zero conditioning for negative (Z-Image uses zeroed negative)
                    "10": {
                        inputs: {
                            conditioning: ["9", 0]
                        },
                        class_type: "ConditioningZeroOut"
                    },
                    // 11. Apply ControlNet (sketch direct OR pose skeleton from DWPose)
                    "11": {
                        inputs: {
                            model: ["1", 0],
                            model_patch: ["4", 0],
                            vae: ["3", 0],
                            image: controlImageSource,  // Either sketch (node 6) or pose (node 17)
                            strength: controlStrength
                        },
                        class_type: "QwenImageDiffsynthControlnet"
                    },
                    // 12. Setup AuraFlow sampling (shift=3)
                    "12": {
                        inputs: {
                            model: ["11", 0],
                            shift: 3
                        },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    // 13. KSampler
                    "13": {
                        inputs: {
                            model: ["12", 0],
                            positive: ["9", 0],
                            negative: ["10", 0],
                            latent_image: ["8", 0],
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            control_after_generate: "randomize",
                            steps: steps, // Progressive: 1→9 as user draws
                            cfg: 1,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: 1
                        },
                        class_type: "KSampler"
                    },
                    // 14. VAE Decode
                    "14": {
                        inputs: {
                            samples: ["13", 0],
                            vae: ["3", 0]
                        },
                        class_type: "VAEDecode"
                    },
                    // 15. Save Image
                    "15": {
                        inputs: {
                            filename_prefix: "ezart_controlnet",
                            images: ["14", 0]
                        },
                        class_type: "SaveImage"
                    }
                }
            };
        } else if (workflowType === 'nunchaku-controlnet' && request.inputImage) {
            // ========== NUNCHAKU 4-BIT CONTROLNET WORKFLOW (Fastest for Doodle) ==========
            // Uses NunchakuZImageDiTLoader for 4-bit quantized model (~3.6GB vs 5.8GB FP8)
            // Optimized for real-time sketch-to-image on Doodle page
            const imageName = await uploadImageToComfyUI(request.inputImage);
            const controlStrength = request.controlnetConditioningScale || 1.0;
            const steps = request.numInferenceSteps || 9;

            console.log('🚀 Using Nunchaku 4-bit ControlNet workflow for Doodle page');

            comfyPayload = {
                prompt: {
                    // 1. Load 4-bit quantized Z-Image model with Nunchaku
                    "1": {
                        inputs: {
                            model_name: "nunchaku\\svdq-int4_r128-z-image-turbo.safetensors"
                        },
                        class_type: "NunchakuZImageDiTLoader"
                    },
                    // 2. Load CLIP/Text Encoder (Qwen)
                    "2": {
                        inputs: {
                            clip_name: "qwen_3_4b.safetensors",
                            type: "lumina2",
                            device: "default"
                        },
                        class_type: "CLIPLoader"
                    },
                    // 3. Load VAE
                    "3": {
                        inputs: {
                            vae_name: "ae.safetensors"
                        },
                        class_type: "VAELoader"
                    },
                    // 4. Load ControlNet model patch
                    "4": {
                        inputs: {
                            name: "Z-Image-Turbo-Fun-Controlnet-Union-2.1-2601-8steps.safetensors"
                        },
                        class_type: "ModelPatchLoader"
                    },
                    // 5. Load control image (user's sketch)
                    "5": {
                        inputs: {
                            image: imageName,
                            upload: "image"
                        },
                        class_type: "LoadImage"
                    },
                    // 6. Scale image to max dimension
                    "6": {
                        inputs: {
                            image: ["5", 0],
                            upscale_method: "lanczos",
                            largest_size: Math.max(width, height)
                        },
                        class_type: "ImageScaleToMaxDimension"
                    },
                    // 7. Get image dimensions for latent
                    "7": {
                        inputs: {
                            image: ["6", 0]
                        },
                        class_type: "GetImageSize"
                    },
                    // 8. Apply ControlNet with QwenImageDiffsynthControlnet
                    "8": {
                        inputs: {
                            model: ["1", 0],
                            model_patch: ["4", 0],
                            vae: ["3", 0],
                            image: ["6", 0],  // Direct sketch input (no Canny preprocessing)
                            strength: controlStrength
                        },
                        class_type: "QwenImageDiffsynthControlnet"
                    },
                    // 9. Encode prompt
                    "9": {
                        inputs: {
                            text: request.prompt,
                            clip: ["2", 0]
                        },
                        class_type: "CLIPTextEncode"
                    },
                    // 10. Zero out negative conditioning
                    "10": {
                        inputs: {
                            conditioning: ["9", 0]
                        },
                        class_type: "ConditioningZeroOut"
                    },
                    // 11. Create empty latent at control image size
                    "11": {
                        inputs: {
                            width: ["7", 0],
                            height: ["7", 1],
                            batch_size: 1
                        },
                        class_type: "EmptySD3LatentImage"
                    },
                    // 12. Apply AuraFlow sampling
                    "12": {
                        inputs: {
                            model: ["8", 0],
                            shift: 3
                        },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    // 13. KSampler - Official Nunchaku settings
                    "13": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: steps,
                            cfg: 1,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: 1,
                            model: ["12", 0],
                            positive: ["9", 0],
                            negative: ["10", 0],
                            latent_image: ["11", 0]
                        },
                        class_type: "KSampler"
                    },
                    // 14. VAE Decode
                    "14": {
                        inputs: {
                            samples: ["13", 0],
                            vae: ["3", 0]
                        },
                        class_type: "VAEDecode"
                    },
                    // 15. Save Image
                    "15": {
                        inputs: {
                            filename_prefix: "ezart_nunchaku_doodle",
                            images: ["14", 0]
                        },
                        class_type: "SaveImage"
                    }
                }
            };
        } else if (workflowType === 'txt2img') {
            // ========== TXT2IMG WORKFLOW ==========
            // Determine steps based on quality setting: Best Quality (UHD) = 9, others = 8
            const qualitySteps = request.resolution === 'Best Quality' || request.resolution === 'UHD' ? 9 : 8;
            const steps = request.numInferenceSteps || qualitySteps;

            comfyPayload = {
                prompt: {
                    "1": {
                        inputs: { unet_name: diffusionModelName, weight_dtype: "default" },
                        class_type: "UNETLoader"
                    },
                    "2": {
                        inputs: { clip_name: "qwen_3_4b.safetensors", type: "lumina2", device: "default" },
                        class_type: "CLIPLoader"
                    },
                    "3": {
                        inputs: { vae_name: "ae.safetensors" },
                        class_type: "VAELoader"
                    },
                    "4": {
                        inputs: { text: request.prompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "5": {
                        inputs: { conditioning: ["4", 0] },
                        class_type: "ConditioningZeroOut"
                    },
                    "6": {
                        inputs: { width, height, batch_size: 1 },
                        class_type: "EmptySD3LatentImage"
                    },
                    "7": {
                        inputs: { model: ["1", 0], shift: 3 },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    "8": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: steps,
                            cfg: 1,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: 1,
                            model: ["7", 0],
                            positive: ["4", 0],
                            negative: ["5", 0],
                            latent_image: ["6", 0]
                        },
                        class_type: "KSampler"
                    },
                    "9": {
                        inputs: { samples: ["8", 0], vae: ["3", 0] },
                        class_type: "VAEDecode"
                    },
                    "10": {
                        inputs: { filename_prefix: "ezart_txt2img", images: ["9", 0] },
                        class_type: "SaveImage"
                    }
                }
            };
        } else if (workflowType === 'img2img') {
            // ========== IMG2IMG WORKFLOW ==========
            // Mirrors Replicate: load image, VAE encode, denoise, decode
            // Output size matches input image size
            const imageName = await uploadImageToComfyUI(request.inputImage!);
            const denoise = request.strength ?? 0.7;
            // Determine steps based on quality setting: Best Quality (UHD) = 9, others = 8
            const qualitySteps = request.resolution === 'Best Quality' || request.resolution === 'UHD' ? 9 : 8;
            const img2imgSteps = request.numInferenceSteps || qualitySteps;

            comfyPayload = {
                prompt: {
                    "1": {
                        inputs: { unet_name: diffusionModelName, weight_dtype: "default" },
                        class_type: "UNETLoader"
                    },
                    "2": {
                        inputs: { clip_name: "qwen_3_4b.safetensors", type: "lumina2", device: "default" },
                        class_type: "CLIPLoader"
                    },
                    "3": {
                        inputs: { vae_name: "ae.safetensors" },
                        class_type: "VAELoader"
                    },
                    "4": {
                        inputs: { text: request.prompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "5": {
                        inputs: { conditioning: ["4", 0] },
                        class_type: "ConditioningZeroOut"
                    },
                    "6": {
                        inputs: { image: imageName, upload: "image" },
                        class_type: "LoadImage"
                    },
                    "7": {
                        inputs: { pixels: ["6", 0], vae: ["3", 0] },
                        class_type: "VAEEncode"
                    },
                    "8": {
                        inputs: { model: ["1", 0], shift: 3 },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    "9": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: img2imgSteps,
                            cfg: 1,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: denoise,
                            model: ["8", 0],
                            positive: ["4", 0],
                            negative: ["5", 0],
                            latent_image: ["7", 0]
                        },
                        class_type: "KSampler"
                    },
                    "10": {
                        inputs: { samples: ["9", 0], vae: ["3", 0] },
                        class_type: "VAEDecode"
                    },
                    "11": {
                        inputs: { filename_prefix: "ezart_img2img", images: ["10", 0] },
                        class_type: "SaveImage"
                    }
                }
            };
        } else if (workflowType === 'inpaint') {
            // ========== INPAINTING WORKFLOW ==========
            const imageName = await uploadImageToComfyUI(request.inputImage!);
            const maskName = await uploadImageToComfyUI(request.maskImage!);
            const denoise = request.strength ?? 1.0; // Full denoise for inpaint by default
            // Determine steps based on quality setting: Best Quality (UHD) = 9, others = 8
            const inpaintQualitySteps = request.resolution === 'Best Quality' || request.resolution === 'UHD' ? 9 : 8;
            const inpaintSteps = request.numInferenceSteps || inpaintQualitySteps;

            comfyPayload = {
                prompt: {
                    "1": {
                        inputs: { unet_name: diffusionModelName, weight_dtype: "default" },
                        class_type: "UNETLoader"
                    },
                    "2": {
                        inputs: { clip_name: "qwen_3_4b.safetensors", type: "lumina2", device: "default" },
                        class_type: "CLIPLoader"
                    },
                    "3": {
                        inputs: { vae_name: "ae.safetensors" },
                        class_type: "VAELoader"
                    },
                    "4": {
                        inputs: { text: request.prompt, clip: ["2", 0] },
                        class_type: "CLIPTextEncode"
                    },
                    "5": {
                        inputs: { conditioning: ["4", 0] },
                        class_type: "ConditioningZeroOut"
                    },
                    "6": {
                        inputs: { image: imageName, upload: "image" },
                        class_type: "LoadImage"
                    },
                    "7": {
                        inputs: { image: maskName, upload: "image" },
                        class_type: "LoadImage"
                    },
                    "7b": {
                        inputs: { image: ["7", 0], channel: "red" },
                        class_type: "ImageToMask"
                    },
                    "8": {
                        inputs: { pixels: ["6", 0], vae: ["3", 0] },
                        class_type: "VAEEncode"
                    },
                    "9": {
                        inputs: { samples: ["8", 0], mask: ["7b", 0] },
                        class_type: "SetLatentNoiseMask"
                    },
                    "10": {
                        inputs: { model: ["1", 0], shift: 3 },
                        class_type: "ModelSamplingAuraFlow"
                    },
                    "11": {
                        inputs: {
                            seed: request.seed || Math.floor(Math.random() * 1000000000),
                            steps: inpaintSteps,
                            cfg: 1,
                            sampler_name: "res_multistep",
                            scheduler: "simple",
                            denoise: denoise,
                            model: ["10", 0],
                            positive: ["4", 0],
                            negative: ["5", 0],
                            latent_image: ["9", 0]
                        },
                        class_type: "KSampler"
                    },
                    "12": {
                        inputs: { samples: ["11", 0], vae: ["3", 0] },
                        class_type: "VAEDecode"
                    },
                    "13": {
                        inputs: { filename_prefix: "ezart_inpaint", images: ["12", 0] },
                        class_type: "SaveImage"
                    }
                }
            };
        }
    } // End of else block (Z-Image-Turbo workflow)

    // Free VRAM if switching from TTS to image generation
    await prepareVRAMForTask('image');

    try {
        const response = await fetch(`${LOCAL_COMFYUI_URL}/prompt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(comfyPayload),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`ComfyUI error: ${response.status} - ${errorText}`);
        }

        const queueResponse = await response.json();
        console.log('📋 ComfyUI queue response:', JSON.stringify(queueResponse, null, 2));

        if (queueResponse.error) {
            throw new Error(`ComfyUI rejected prompt: ${queueResponse.error}`);
        }

        const prompt_id = queueResponse.prompt_id;
        if (!prompt_id) {
            throw new Error('ComfyUI did not return a prompt_id');
        }
        console.log(`⏳ ComfyUI prompt queued: ${prompt_id}`);

        // Poll for completion - first run takes longer due to model loading
        let result: Blob | null = null;
        const maxPolls = 900; // 5 minutes max (model swapping can be slow)
        for (let i = 0; i < maxPolls; i++) {
            await new Promise(r => setTimeout(r, 200));

            const historyRes = await fetch(`${LOCAL_COMFYUI_URL}/history/${prompt_id}`);
            const history = await historyRes.json();

            // Debug: Log polling status every 5 iterations (more frequent)
            if (i % 5 === 0) {
                console.log(`🔄 Poll #${i}/${maxPolls}: history keys =`, Object.keys(history), 'prompt_id =', prompt_id);
                if (history[prompt_id]) {
                    console.log('  -> status:', history[prompt_id].status?.status_str);
                    // Log any execution progress
                    if (history[prompt_id].status?.messages) {
                        const lastMsg = history[prompt_id].status.messages.slice(-1)[0];
                        if (lastMsg) console.log('  -> last msg:', lastMsg[0]);
                    }
                }
            }

            // Check for execution error
            if (history[prompt_id]?.status?.status_str === 'error') {
                const errorMsg = history[prompt_id].status?.messages?.find(
                    (m: any) => m[0] === 'execution_error'
                )?.[1]?.exception_message || 'Unknown ComfyUI execution error';
                throw new Error(`ComfyUI execution failed: ${errorMsg.substring(0, 500)}`);
            }

            if (history[prompt_id]?.outputs) {
                console.log('✅ Found outputs! Nodes with output:', Object.keys(history[prompt_id].outputs));
                for (const nodeId in history[prompt_id].outputs) {
                    const output = history[prompt_id].outputs[nodeId];
                    console.log(`  Node ${nodeId}:`, output.images ? `${output.images.length} images` : 'no images');
                    if (output.images?.[0]) {
                        const img = output.images[0];
                        console.log('  -> Image found:', img);
                        const imageUrl = `${LOCAL_COMFYUI_URL}/view?filename=${encodeURIComponent(img.filename)}&subfolder=${encodeURIComponent(img.subfolder || '')}&type=${img.type || 'output'}`;
                        console.log('  -> Fetching from:', imageUrl);
                        const imageRes = await fetch(imageUrl);
                        result = await imageRes.blob();
                        console.log('  -> Blob size:', result.size);
                        break;
                    }
                }
                if (result) break;
            }
        }

        if (!result) {
            throw new Error('ComfyUI generation timed out after 3 minutes. Check ComfyUI console for errors.');
        }

        console.log('✅ Local generation complete!', result.size, 'bytes');
        return {
            imageBlob: result,
            model: 'local-comfyui',
            prompt: request.prompt,
        };
    } catch (error: any) {
        console.error('❌ Local ComfyUI error:', error.message);
        throw error;
    }
}

// Helper to upload an image to ComfyUI
async function uploadImageToComfyUI(imageSource: string): Promise<string> {
    let blob: Blob;

    if (imageSource.startsWith('http://') || imageSource.startsWith('https://')) {
        // Fetch image from URL (R2, etc.)
        console.log('📥 Fetching image from URL for ComfyUI upload:', imageSource.substring(0, 80));
        const response = await fetch(imageSource);
        if (!response.ok) {
            throw new Error(`Failed to fetch image from URL: ${response.status}`);
        }
        blob = await response.blob();
    } else {
        // Handle base64 data URL
        const imageData = imageSource.replace(/^data:image\/\w+;base64,/, '');
        const binaryString = atob(imageData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: 'image/png' });
    }

    const formData = new FormData();
    formData.append('image', blob, `input_${Date.now()}.png`);

    const response = await fetch(`${LOCAL_COMFYUI_URL}/upload/image`, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        throw new Error('Failed to upload image to ComfyUI');
    }

    const result = await response.json();
    console.log('✅ Uploaded to ComfyUI:', result.name);
    return result.name;
}

async function generateWithReplicate(request: GenerationRequest): Promise<GenerationResult> {
    let modelId = request.model || 'prunaai/z-image-turbo';
    const config = getModelConfig(modelId);

    // ... (config warning)

    // Automatic Model Switching logic removed for Z-Image-Turbo to enforce txt2img only per user request.

    const MAX_RETRIES = 3;
    let attempt = 0;

    // 1. Optimize Prompt (Model Specific + User)
    // Skip prefix optimization if there's a character reference (prompt is already styled)
    const hasCharacterRef = request.characterReference && request.characterReference.snapshotUrl;
    let prompt = hasCharacterRef ? request.prompt : optimizePrompt(request.prompt, modelId);

    // 2. Optimization: Negative Prompt (only for models that support it)
    const supportsNegative = config?.supportsNegativePrompt !== false; // Default true
    let negativePrompt = supportsNegative ? getNegativePrompt(undefined, modelId) : undefined;

    // BACKEND-ONLY SAFETY ENFORCEMENT
    if (request.selections?.rating && supportsNegative) {
        // ... (existing safety logic)
        const rating = request.selections.rating;
        if (rating === 'NSFW' || rating === 'Explicit') {
            prompt += ", mature";
            const safetyNegatives = "child, children, kid, kids, baby, toddler, minor, underage, prepubescent, young, loli, shota, high school uniform, school uniform";
            negativePrompt = `${negativePrompt}, ${safetyNegatives}`;
        }
    }

    console.log('📡 Calling edge function with:', { model: modelId, prompt: prompt.substring(0, 30), aspectRatio: request.aspectRatio, supportsNegative });

    const body: Record<string, any> = {
        prompt: prompt,
        model: modelId,
        negative_prompt: supportsNegative ? negativePrompt : undefined,
        character_reference: request.characterReference ? {
            snapshot_url: request.characterReference.snapshotUrl,
            snapshot_urls: request.characterReference.snapshotUrls, // Multiple images for better identity
            dna_metadata: request.characterReference.metadata,
            name: request.characterReference.name
        } : undefined,
        seed: request.seed,
        output_size: request.outputSize, // Pass output resolution to edge function (1K, 2K, 4K)
        user_id: request.userId, // For credit deduction
    };

    // Model Specific Parameter handling
    if (modelId === 'google/nano-banana' || modelId === 'google/nano-banana-pro' || modelId === 'models/gemini-3-pro-image-preview') {
        body.aspect_ratio = request.aspectRatio || '9:16';
        // Map resolution to output_size for Google models (1080p→1K, 2K→2K, 4K→4K)
        if (request.resolution) {
            const resolutionMap: Record<string, string> = { '1080p': '1K', '2K': '2K', '4K': '4K' };
            body.output_size = resolutionMap[request.resolution] || '2K';
        }
        // Pass strength for img2img - will be translated to prompt modifications in edge function
        if (request.inputImage && request.strength !== undefined) {
            body.strength = request.strength;
            console.log(`Using google img2img with strength: ${request.strength}`);
        }
        console.log(`Using Google ${modelId} model with output_size: ${body.output_size || 'default'}`);
    } else if (modelId === 'prunaai/z-image-turbo-controlnet') {
        // Z-Image-Turbo ControlNet for sketch-to-image
        const { width, height } = getDimensionsFor2MP(request.aspectRatio, request.resolution);
        body.width = width;
        body.height = height;
        body.num_inference_steps = request.numInferenceSteps || 9; // Progressive: 1→9 as user draws
        body.guidance_scale = 0;
        // Pass ControlNet conditioning scale
        if (request.controlnetConditioningScale !== undefined) {
            body.controlnet_conditioning_scale = request.controlnetConditioningScale;
        }
        console.log(`Using ${modelId} with ControlNet scale: ${request.controlnetConditioningScale ?? 0.75}, steps: ${body.num_inference_steps}`);
    } else if (modelId === 'prunaai/z-image-turbo' || modelId === 'prunaai/z-image-turbo-img2img' || modelId === 'prunaai/z-image-turbo-lora') {
        // Z-Image-Turbo requires explicit width/height for 2MP
        const { width, height } = getDimensionsFor2MP(request.aspectRatio, request.resolution);
        body.width = width;
        body.height = height;
        // It might not accept 'aspect_ratio' string, so we send dimensions
        body.num_inference_steps = 9; // Default for turbo
        body.guidance_scale = 0; // Recommended for turbo

        // For img2img, allow custom strength (lower = more preservation of input)
        if ((modelId === 'prunaai/z-image-turbo-img2img' || modelId === 'prunaai/z-image-turbo-lora') && request.strength !== undefined) {
            body.strength = request.strength;
            console.log(`Using img2img strength: ${request.strength}`);
        }

        console.log(`Using ${modelId} with 2MP dimensions: ${width}x${height}`);
    } else {
        body.aspect_ratio = request.aspectRatio || '16:9';
    }

    // Dynamic Input Image Mapping
    // Dynamic Input Image Mapping
    if (request.inputImage && config?.inputs?.inputKeyMap) {
        // Resolve Blob URLs to Base64
        let finalInputImage = request.inputImage;
        if (finalInputImage.startsWith('blob:')) {
            try {
                const blobRes = await fetch(finalInputImage);
                const blob = await blobRes.blob();
                finalInputImage = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (err) {
                console.warn('Failed to convert blob URL to base64:', err);
            }
        }

        // Find which input key maps to 'inputImage' in our schema
        // The config maps: { 'inputImage': 'actual_api_key' }
        const apiKey = config.inputs.inputKeyMap['inputImage'];
        if (apiKey) {
            body[apiKey] = finalInputImage;
            console.log(`📎 Mapped inputImage to '${apiKey}' for model ${modelId}`);
        }
    } else if (request.inputImage && !config?.inputs?.inputKeyMap) {
        // Fallback: If no inputKeyMap defined but we have an inputImage, log a warning
        console.warn(`⚠️ No inputKeyMap defined for model ${modelId}, inputImage may not be sent correctly`);
    }

    // Process Mask Image
    if (request.maskImage) {
        // Automatic Model Switching for Z-Image Turbo Inpainting
        if (modelId === 'prunaai/z-image-turbo') {
            modelId = 'prunaai/z-image-turbo-inpaint';
            console.log('🎭 Switching to proper inpainting model: prunaai/z-image-turbo-inpaint');
        }

        let finalMaskImage = request.maskImage;
        if (finalMaskImage.startsWith('blob:')) {
            try {
                const blobRes = await fetch(finalMaskImage);
                const blob = await blobRes.blob();
                finalMaskImage = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch (err) {
                console.warn('Failed to convert mask blob URL to base64:', err);
            }
        }
        // Standardize mask key for Edge Function
        body.mask = finalMaskImage;
        if (request.editMode) {
            body.edit_mode = request.editMode;
        }
        // CRITICAL: Update the model in the body after potential switch
        body.model = modelId;

        // CRITICAL: For inpainting, we MUST include the input image
        // The inpaint model requires both 'image' and 'mask_image'
        if (request.inputImage && modelId === 'prunaai/z-image-turbo-inpaint') {
            let finalInputImage = request.inputImage;
            if (finalInputImage.startsWith('blob:')) {
                try {
                    const blobRes = await fetch(finalInputImage);
                    const blob = await blobRes.blob();
                    finalInputImage = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                } catch (err) {
                    console.warn('Failed to convert input image blob URL to base64:', err);
                }
            }
            body.image = finalInputImage;
            console.log('🖼️ Added input image for inpainting');
        }
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const functionUrl = `${supabaseUrl}/functions/v1/generate`;

    while (attempt < MAX_RETRIES) {
        attempt++;
        try {
            console.log(`🌐 Function request (Attempt ${attempt}/${MAX_RETRIES}):`, functionUrl);

            // Use longer timeout for 4K images (they can be 10-20MB)
            const is4K = body.output_size === '4K' || request.outputSize === '4K';
            // Increase timeout for Gemini 3 Pro which can be slower (up to 3 mins for 4K)
            const timeoutMs = is4K ? 180000 : 120000; // 180s for 4K, 120s otherwise
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                    'apikey': supabaseAnonKey
                },
                body: JSON.stringify(body),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            console.log(`📥 Response status (Attempt ${attempt}):`, response.status, response.statusText);

            // Handle Rate Limiting (429) and Gateway Errors (502 - often masks upstream 429s)
            if (response.status === 429 || response.status === 502) {
                const retryHeader = response.headers.get('Retry-After');
                const waitTime = retryHeader ? (parseInt(retryHeader) * 1000 + 500) : 2000 * attempt; // Backoff
                console.warn(`⚠️ Rate limited or Server Error (HTTP ${response.status}). Retrying in ${waitTime}ms...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
            }

            // Handle insufficient credits (402 Payment Required)
            if (response.status === 402) {
                const errorData = await response.json().catch(() => ({}));
                const err = new Error('insufficient_credits') as any;
                err.code = 'INSUFFICIENT_CREDITS';
                err.required = errorData.required || 0;
                err.balance = errorData.balance || 0;
                throw err;
            }

            if (!response.ok) {
                const errorText = await response.text();
                // Check for rate limit keywords in body even if status wasn't caught (e.g. 400 or 500 wrapping it)
                if (attempt < MAX_RETRIES && (errorText.includes('throttled') || errorText.includes('rate limit'))) {
                    console.warn(`⚠️ Error indicates throttling: ${errorText.substring(0, 100)}. Retrying...`);
                    await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
                    continue;
                }

                console.error('❌ Error response:', errorText);
                throw new Error(errorText || `HTTP ${response.status}`);
            }

            // 1. Check if response is actually JSON first (even if 200 OK)
            const contentType = response.headers.get('content-type');
            console.log('📦 Response content-type:', contentType);

            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                console.log('✅ Received JSON response:', JSON.stringify(data, null, 2));

                if (data.output && Array.isArray(data.output) && data.output.length > 0) {
                    // Replicate standard array output
                    console.log('🖼️ Returning image URL from array:', data.output[0]);
                    return {
                        imageUrl: data.output[0],
                        model: modelId,
                        prompt: request.prompt
                    };
                }

                if (typeof data.output === 'string' && data.output.startsWith('http')) {
                    // Replicate single string output
                    console.log('🖼️ Returning image URL from string:', data.output);
                    return {
                        imageUrl: data.output,
                        model: modelId,
                        prompt: request.prompt
                    };
                }

                // If it's an error in 200 OK (rare but possible)
                if (data.error) {
                    console.error('❌ Error in 200 OK response:', data.error);
                    throw new Error(data.error);
                }

                // If we got here, the JSON response doesn't match expected format
                console.warn('⚠️ Unexpected JSON response format. Full data:', data);
            }

            // 2. Fallback to Blob handling (Standard binary response)
            const blob = await response.blob();
            console.log('✅ Received blob:', blob.size, 'bytes, type:', blob.type);

            if (blob.type === 'application/json' || blob.size < 500) {
                try {
                    const text = await blob.text();
                    console.log('📄 Blob text content:', text.substring(0, 500));
                    const errorData = JSON.parse(text);
                    if (errorData.error) {
                        // Check inside JSON error
                        if (attempt < MAX_RETRIES && (JSON.stringify(errorData.error).includes('throttled') || JSON.stringify(errorData.error).includes('rate limit'))) {
                            console.warn(`⚠️ JSON Error indicates throttling. Retrying...`);
                            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
                            continue;
                        }
                        console.error('❌ Error in blob JSON:', errorData.error);
                        throw new Error(errorData.error);
                    }
                } catch (parseError) {
                    console.log('⚠️ Blob parse error (may be normal for image data):', parseError);
                    // Not JSON or parse error, stick with the blob
                }
            }

            console.log('🖼️ Returning blob as imageBlob');
            return {
                imageBlob: blob,
                model: modelId,
                prompt: request.prompt
            };

        } catch (error: any) {
            console.error(`❌ Attempt ${attempt} failed:`, error);
            // Retry on: throttling, rate limits, gateway errors, network failures, or timeouts
            const isRetryable = error.message?.includes('throttled') ||
                error.message?.includes('rate limit') ||
                error.message?.includes('502') ||
                error.message?.includes('Failed to fetch') ||
                error.message?.includes('network') ||
                error.message?.includes('aborted') ||
                error.name === 'TypeError' || // Network errors often throw TypeError
                error.name === 'AbortError'; // Timeout via AbortController

            if (attempt < MAX_RETRIES && isRetryable) {
                const isNetworkIssue = error.message?.includes('Failed to fetch') || error.name === 'AbortError';
                const delay = isNetworkIssue ? 3000 : 2000; // Longer delay for network/timeout issues
                console.log(`🔄 Retrying in ${delay}ms... (attempt ${attempt + 1}/${MAX_RETRIES})`);
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }
            throw error;
        }
    }
    throw new Error(`Generation failed after ${MAX_RETRIES} attempts.`);
}

/**
 * Pollinations.ai - Free, unlimited, no auth
 */
async function generateWithPollinations(request: GenerationRequest): Promise<GenerationResult> {
    const width = request.width || 1024;
    const height = request.height || 576;
    const seed = request.seed || Math.floor(Math.random() * 1000000);

    const encodedPrompt = encodeURIComponent(request.prompt);
    const url = `https://image.pollinations.ai/prompt/${encodedPrompt}?width=${width}&height=${height}&seed=${seed}&nologo=true`;

    console.log('📡 Calling Pollinations:', url);

    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Pollinations generation failed');
    }

    const blob = await response.blob();
    console.log('✅ Received image from Pollinations:', blob.size, 'bytes');

    return {
        imageBlob: blob,
        imageUrl: url,
        model: 'pollinations',
        prompt: request.prompt
    };
}

// ============================================
async function generateWithChatGPT(request: GenerationRequest): Promise<GenerationResult> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sign in to ezart to use ChatGPT image generation.');
    const references = [...new Set([
        ...(request.inputImage ? [request.inputImage] : []),
        ...(request.referenceImages || []),
        ...(request.characterReference?.snapshotUrls || (request.characterReference?.snapshotUrl ? [request.characterReference.snapshotUrl] : [])),
    ])];
    if (references.length > 16) throw new Error('ChatGPT supports up to 16 images, including character references.');
    const images: string[] = [];
    for (const reference of references) images.push(await prepareCodexReference(reference));
    const quality = request.resolution === 'Extra High' ? 'xhigh' : request.resolution === 'Max' ? 'max' : ['Low', 'Fast'].includes(request.resolution || '') ? 'low' : ['High', 'Best Quality'].includes(request.resolution || '') ? 'high' : request.model === 'openai/gpt-image-2.5-sunburst' ? 'high' : 'medium';
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-chatgpt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
        body: JSON.stringify({ model: chatGPTBackendModel(request.model), prompt: request.prompt, aspect_ratio: request.aspectRatio || '1:1', quality, reference_images: images, character_name: request.characterReference?.name,
            ...(request.maskImage ? { mask: await inlineCodexReference(request.maskImage) } : {}) }),
        // No automatic generation retries: a timeout could still have created an image.
        signal: AbortSignal.timeout(150_000),
    });
    if (!response.ok) {
        const failure = await response.json().catch(() => ({ error: 'ChatGPT image generation failed.' }));
        const error = new Error(failure.error || 'ChatGPT image generation failed.') as Error & { code?: string; required?: number; balance?: number };
        error.code = response.status === 402 ? 'INSUFFICIENT_CREDITS' : failure.code;
        error.required = failure.required;
        error.balance = failure.balance;
        throw error;
    }
    const blob = await response.blob();
    if (!blob.type.startsWith('image/') || !blob.size) throw new Error('ChatGPT returned no image.');
    return { imageBlob: blob, model: request.model || DEFAULT_IMAGE_MODEL, prompt: request.prompt };
}

// MAIN GENERATOR
// ============================================

export async function generateImage(request: GenerationRequest | string): Promise<GenerationResult>;
export async function generateImage(request: GenerationRequest | string): Promise<GenerationResult> {
    // Handle string prompts (simplified API)
    if (typeof request === 'string') {
        request = {
            prompt: request,
            model: DEFAULT_IMAGE_MODEL,
            aspectRatio: '16:9'
        };
    }

    const model = request.model || DEFAULT_IMAGE_MODEL;

    console.log(`🎨 Generating with ${model}:`, request.prompt.substring(0, 50) + '...');

    // ============================================
    // LOCAL INFERENCE CHECK (Development Only)
    // ============================================
    // If USE_LOCAL_INFERENCE is enabled, route supported models to local ComfyUI
    // To disable: remove VITE_USE_LOCAL_INFERENCE from .env.local and restart dev server
    if (USE_LOCAL_INFERENCE) {
        const isLocalSupported = model.startsWith('tongyi-mai/z-image') ||    // Z-Image Foundation (local only for now)
            model.startsWith('prunaai/z-image-turbo') ||
            model.startsWith('cagliostrolab/animagine') ||
            model.startsWith('nunchaku/'); // Nunchaku 4-bit models (local only)
        if (isLocalSupported) {
            console.log('🏠 Routing to LOCAL ComfyUI (VITE_USE_LOCAL_INFERENCE=true)');
            try {
                return await generateWithLocalComfyUI(request);
            } catch (err: any) {
                // If ControlNet is not supported locally, fall back to Replicate
                if (err.message === 'LOCAL_CONTROLNET_NOT_SUPPORTED') {
                    console.log('☁️ ControlNet not supported locally, falling back to Replicate API...');
                    return generateWithReplicate(request);
                }
                throw err;
            }
        }
    }

    // Local-only models - error if local inference is disabled
    if (model.startsWith('nunchaku/')) {
        throw new Error(`Nunchaku models require local inference. Set VITE_USE_LOCAL_INFERENCE=true in .env.local`);
    }
    if (model.startsWith('tongyi-mai/z-image')) {
        throw new Error(`Z-Image Foundation is local-only (Replicate not yet available). Set VITE_USE_LOCAL_INFERENCE=true in .env.local`);
    }

    switch (model) {
        case QWEN21_MODEL_ID:
            return generateWithQwen21(request);
        case DEFAULT_IMAGE_MODEL:
            return generateWithChatGPT(request);
        case 'google/nano-banana':
        case 'google/nano-banana-pro':
        case 'models/gemini-3-pro-image-preview':
        case 'prunaai/z-image-turbo':
        case 'prunaai/z-image-turbo-inpaint':
        case 'prunaai/z-image-turbo-img2img':
        case 'prunaai/z-image-turbo-controlnet':
        case 'prunaai/z-image-turbo-lora':
        case 'cagliostrolab/animagine-xl-3.1':
            return generateWithReplicate(request);

        default:
            if (getModelConfig(model)?.provider === 'chatgpt') return generateWithChatGPT(request);
            throw new Error(`Unknown model: ${model}`);
    }
}

export async function generateImageBlob(
    prompt: string,
    model?: ModelId,
    options?: {
        aspectRatio?: '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '21:9' | '9:21' | '3:2' | '2:3' | '4:5' | '5:4',
        inputImage?: string,
        referenceImages?: string[],
        selections?: Record<string, string | undefined>,
        characterReference?: { name: string, snapshotUrl: string, snapshotUrls?: string[], metadata: any },
        resolution?: string
    }
): Promise<Blob> {
    const result = await generateImage({
        prompt,
        model,
        aspectRatio: options?.aspectRatio,
        inputImage: options?.inputImage,
        referenceImages: options?.referenceImages,
        selections: options?.selections,
        characterReference: options?.characterReference,
        resolution: options?.resolution as any
    });

    if (result.imageBlob) {
        return result.imageBlob;
    }

    if (result.imageUrl) {
        const response = await fetch(result.imageUrl);
        return response.blob();
    }

    throw new Error('No image data returned');
}

export async function editImage(prompt: string, inputImageUrl: string): Promise<Blob> {
    // Use Z-Image Turbo img2img for image editing
    const result = await generateImage({
        prompt,
        model: 'prunaai/z-image-turbo-img2img',
        inputImage: inputImageUrl
    });

    if (result.imageBlob) {
        return result.imageBlob;
    }

    if (result.imageUrl) {
        const response = await fetch(result.imageUrl);
        return response.blob();
    }

    throw new Error('No image data returned');
}

/**
 * Inpaint an image using a mask
 * @param prompt Prompt describing the change or the whole image
 * @param inputImageUrl Base image URL
 * @param maskImageUrl Mask image URL (white = edit, black = keep)
 * @param model Model to use (defaults to Z-Image Turbo Inpaint)
 */
export async function inpaintImage(
    prompt: string,
    inputImageUrl: string,
    maskImageUrl: string,
    model: ModelId = 'prunaai/z-image-turbo-inpaint',
    aspectRatio?: '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '21:9' | '9:21' | '3:2' | '2:3' | '4:5' | '5:4'
): Promise<Blob | string> {
    console.log('🎨 Inpainting with', model);
    const result = await generateImage({
        prompt,
        model,
        aspectRatio,
        inputImage: inputImageUrl,
        maskImage: maskImageUrl,
        editMode: 'inpainting'
    });

    if (result.imageBlob) {
        return result.imageBlob;
    }

    if (result.imageUrl) {
        return result.imageUrl;
    }

    throw new Error('No inpainting result returned');
}

/**
 * Generate an anime-style image from a sketch using ControlNet
 * Used by the Doodle page for real-time sketch-to-anime generation
 *
 * @param sketchBase64 The user's sketch as a base64 data URL
 * @param prompt Text prompt describing the desired output
 * @param options Generation options including control strength and model
 */
export async function generateFromSketch(
    sketchBase64: string,
    prompt: string,
    options?: {
        controlStrength?: number; // 0.3-1.0, default 0.75 - higher = more faithful to sketch
        model?: ModelId;
        aspectRatio?: '1:1' | '16:9' | '9:16' | '4:3' | '3:4' | '3:2' | '2:3' | '4:5' | '5:4';
        maskImage?: string; // For inpainting mode
        steps?: number; // 1-9, for progressive drawing (starts rough, gets refined)
        poseMode?: boolean; // Extract pose from image (for camera + character mode)
        characterRef?: { // Character reference for pose transfer
            id: string;
            name: string;
            imageUrl: string;
        };
    }
): Promise<Blob | string> {
    // Default to ControlNet model for sketch-to-image
    const model = options?.model || 'prunaai/z-image-turbo-controlnet';
    const controlStrength = options?.controlStrength ?? 0.75;
    const aspectRatio = options?.aspectRatio || '1:1';

    // Add anime-optimized prefix to prompt
    let animePrompt = `masterpiece, best quality, ${prompt}`;

    // If pose mode with character, emphasize character appearance
    if (options?.poseMode && options?.characterRef) {
        animePrompt = `masterpiece, best quality, ${options.characterRef.name}, ${prompt}, same character, consistent appearance`;
        console.log('🎭 Pose Transfer Mode - Character:', options.characterRef.name);
    }

    const steps = options?.steps; // Progressive: 1→9 as user draws

    console.log('🎨 Generating from sketch with', model, 'controlStrength:', controlStrength, 'steps:', steps || 9, 'poseMode:', options?.poseMode || false);

    // Build the request
    const request: GenerationRequest = {
        prompt: animePrompt,
        model,
        aspectRatio,
        inputImage: sketchBase64, // Will be mapped to control_image by the backend
        controlnetConditioningScale: controlStrength,
        numInferenceSteps: steps, // Progressive steps for real-time drawing
        poseMode: options?.poseMode, // Flag for pose extraction
        characterReference: options?.characterRef ? {
            name: options.characterRef.name,
            snapshotUrl: options.characterRef.imageUrl,
            metadata: { id: options.characterRef.id },
        } : undefined,
    };

    // Add mask for inpainting mode (switches to inpaint model)
    if (options?.maskImage) {
        request.maskImage = options.maskImage;
        request.editMode = 'inpainting';
        request.model = 'prunaai/z-image-turbo-inpaint';
    }

    const result = await generateImage(request);

    if (result.imageBlob) {
        return result.imageBlob;
    }

    if (result.imageUrl) {
        return result.imageUrl;
    }

    throw new Error('No sketch generation result returned');
}

// ============================================
// HEAD TRANSPOSITION
// ============================================

/**
 * Transposes AI-generated head onto body with original head
 * Uses nano-banana (fast model) for head replacement
 *
 * Workflow:
 * 1. Body generated WITH normal head
 * 2. User crops and generates detailed head
 * 3. This function composites the AI head onto the body
 * 4. User confirms the result before reference sheet generation
 */
export interface HeadTranspositionRequest {
    bodyImageUrl: string;        // Body with original generic head
    headImageUrl: string;        // AI-generated detailed head
    bodyImageBase64?: string;    // Optional: base64 data to skip URL fetch (avoids CORS)
    headImageBase64?: string;    // Optional: base64 data to skip URL fetch (avoids CORS)
    characterDescription: string; // User's character description
    form: 'humanoid' | 'creature';
    quality?: 'HQ' | 'HD';
    userId: string;              // For R2 upload
    projectId: string;           // For R2 upload path
    characterId: string;         // For R2 upload path
    cropCoordinates?: {          // Optional: head crop region for better alignment
        x: number;
        y: number;
        width: number;
        height: number;
    };
}

// ============================================
// BODY GENERATION WITH HEAD REFERENCE
// ============================================
// Generates a full body image while using a head portrait as reference
// to ensure face/skin tone consistency between separate generations

export interface BodyWithHeadReferenceRequest {
    bodyPrompt: string;           // The body generation prompt
    headImageUrl: string;         // URL of the head portrait to reference
    headImageBase64?: string;     // Optional base64 to avoid CORS
    referenceImages?: string[];   // Additional body references; approved head remains first
    aspectRatio: '2:3' | '9:16' | '16:9' | '1:1';
    sharedAttributes: {
        gender: string;
        skinTone: string;
        ethnicity?: string;
    };
}

export interface BodyWithHeadReferenceResult {
    imageUrl?: string;
    imageBlob?: Blob;
}

export async function generateBodyWithHeadReference(
    request: BodyWithHeadReferenceRequest
): Promise<BodyWithHeadReferenceResult> {
    const generationPrompt = `Generate a complete full-body character image (head to feet, 2:3 portrait canvas).

CHARACTER DESCRIPTION:
${request.bodyPrompt}

CRITICAL - FACE CONSISTENCY:
Look at the provided reference portrait (Image 1). The full body character MUST have:
- The EXACT SAME face as shown in Image 1
- The EXACT SAME skin tone/surface texture as Image 1
- The EXACT SAME hair color and style as Image 1
- Matching eyes, nose, lips, and facial structure

The reference portrait shows ONLY the face/head. Generate a complete full body (head to feet) of this same character.

ANATOMY AND FRAMING:
- Prioritize natural anatomy appropriate to this character and its chosen art style.
- Preserve natural head-to-body scale, torso length, and limb proportions.
- Show the complete figure with a small comfortable margin above the head and below the feet.
- Adjust camera distance to fit the figure. Never compress, stretch, shorten limbs, or enlarge the head to fill the canvas.
- Use the reference portrait for facial identity, hairstyle, and coloring only; do not copy its close-up framing or relative head scale.

The body should be standing in a neutral pose facing forward, wearing the coverage described in the prompt, on a plain light grey background (#D4D4D4).

CONSISTENCY REQUIREMENTS:
- Create a NEW full body image of the SAME character shown in the reference portrait
- The face in the full body MUST perfectly match the reference portrait
- Maintain identical facial features, hair, and character details

Output: 2:3 portrait showing the complete figure at natural proportions with comfortable margins.`;

    const reference = await prepareCodexReference(request.headImageBase64 || request.headImageUrl);
    const bodyReferences = await Promise.all((request.referenceImages || []).map(prepareCodexReference));
    const result = await generateImage({
        model: 'openai/gpt-image-2.5-sunburst', prompt: generationPrompt,
        aspectRatio: '2:3', resolution: 'High', referenceImages: [reference, ...bodyReferences]
    });
    if (!result.imageBlob) throw new Error('Sunburst returned no character body image.');
    return { imageBlob: result.imageBlob };
}

export interface HeadTranspositionResult {
    imageUrl: string;            // URL of transposed body with new head
    imageBlob?: Blob;            // Blob if available
}

export async function transposeHeadOntoBody(
    request: HeadTranspositionRequest
): Promise<HeadTranspositionResult> {
    const transpositionPrompt = `Create a new full-body character image by combining elements from the two provided images.

TASK: Replace the HEAD (face + hair) on Image 1's body with the HEAD from Image 2. Preserve facial details with PIXEL-LEVEL FIDELITY.

Image 1 (full-body) provides:
- The ENTIRE BODY including shoulders, neck, and ALL clothing (bikini top straps, etc.)
- The pose, proportions, and grey background
- The SKIN TONE / FUR / SCALES texture that must be used throughout

Image 2 (portrait) provides:
- THE SOURCE OF TRUTH for the face - every detail matters
- THE DEFINITIVE HAIRSTYLE - this is the correct hair length, style, and color

🎯 CRITICAL - FACIAL DETAIL PRESERVATION (HIGHEST PRIORITY):
Copy Image 2's face with EXACT fidelity. Do NOT simplify, stylize, or reinterpret ANY feature:

EYES (copy EXACTLY):
- Exact eye SHAPE (round, almond, hooded, monolid, etc.)
- Exact eye SIZE and spacing
- Exact iris COLOR and pattern
- Exact pupil size
- Eyelash length, thickness, curl
- Under-eye details (bags, lines, highlights)
- Eye corners (inner/outer canthus shape)

EYEBROWS (copy EXACTLY):
- Exact brow SHAPE (arched, straight, rounded)
- Exact brow THICKNESS and density
- Exact brow COLOR
- Exact brow POSITION relative to eyes
- Individual hair direction if visible

NOSE (copy EXACTLY):
- Exact nose SHAPE (bridge width, tip shape)
- Exact nostril shape and size
- Exact nose length and projection
- Highlight and shadow patterns

MOUTH & LIPS (copy EXACTLY):
- Exact lip SHAPE (cupid's bow, corners)
- Exact lip FULLNESS (upper vs lower ratio)
- Exact lip COLOR and texture
- Exact expression/position (open, closed, smile)
- Teeth visibility if any

FACIAL STRUCTURE (copy EXACTLY):
- Exact jaw shape and chin
- Exact cheekbone prominence
- Exact face width and proportions
- Exact forehead shape

SKIN DETAILS (copy EXACTLY):
- Freckles, moles, beauty marks - EXACT positions
- Scars, birthmarks - preserve all
- Skin texture and pores
- Blush/flush areas

CRITICAL COMPOSITION RULES:
1. KEEP Image 1's shoulders, neck, and chest area COMPLETELY UNCHANGED
2. KEEP ALL clothing from Image 1 intact - bikini straps, top, everything
3. Match the SKIN TONE from Image 1's body - blend at jaw/neck only
4. NO visible seams, edges, or color mismatches at ANY transition point

⚠️ HAIR CONSISTENCY FIX:
- Image 2's hair is the CORRECT hairstyle
- If Image 1 shows different hair (e.g., long hair but Image 2 has short):
  • REMOVE mismatching hair from Image 1's body
  • Paint over with skin/background as appropriate
- Final result should ONLY show hair matching Image 2

WHAT TO AVOID:
- Do NOT simplify or "clean up" facial features
- Do NOT change eye shape, size, or expression
- Do NOT alter nose or lip proportions
- Do NOT remove freckles, moles, or skin details
- Do NOT copy Image 2's neck, shoulders, or chest
- Do NOT change Image 1's body skin tone
- Do NOT leave mismatched hair visible

The face in the final image should be INDISTINGUISHABLE from Image 2's face - same person, same details, same expression. Only the skin tone may be adjusted to match the body.

Preserve the body reference's anatomical proportions and head-to-body scale. Fit the complete figure with comfortable margins; never compress or stretch it. Output aspect ratio: 2:3 full body portrait.`;

    const references = await Promise.all([
        prepareCodexReference(request.bodyImageBase64 || request.bodyImageUrl),
        prepareCodexReference(request.headImageBase64 || request.headImageUrl)
    ]);
    const result = await generateImage({
        model: 'openai/gpt-image-2.5-sunburst', prompt: transpositionPrompt,
        aspectRatio: '2:3', resolution: 'High', referenceImages: references
    });
    if (!result.imageBlob) throw new Error('Sunburst returned no combined character image.');
    const imageUrl = await saveGeneratedImageToR2(undefined, result.imageBlob,
        request.userId, request.projectId, request.characterId, 'body', false);
    return { imageUrl, imageBlob: result.imageBlob };
}

// ============================================
// MULTI-TURN CONVERSATION IMAGE EDITING (PRO MODE)
// ============================================

/** Edit the existing character image through the private Sunburst connection. */
export interface ConversationEditRequest {
    existingImageUrl: string;    // URL of the image to edit
    existingImageBase64?: string; // Optional: pre-fetched base64 to avoid CORS issues
    editPrompt: string;          // What changes to make
    aspectRatio?: '1:1' | '9:16' | '16:9' | '3:4' | '4:3' | '2:3' | '3:2';
    outputSize?: '1K' | '2K' | '4K';
}

export interface ConversationEditResult {
    imageBase64: string;
    imageBlob: Blob;
    mimeType: string;
}

export async function editImageWithConversation(
    request: ConversationEditRequest
): Promise<ConversationEditResult> {
    const reference = await prepareCodexReference(request.existingImageBase64 || request.existingImageUrl);
    const result = await generateImage({
        model: 'openai/gpt-image-2.5-sunburst',
        prompt: request.editPrompt,
        aspectRatio: request.aspectRatio || '1:1', resolution: 'High',
        referenceImages: [reference]
    });
    if (!result.imageBlob) throw new Error('Sunburst returned no edited character image.');
    const url = URL.createObjectURL(result.imageBlob);
    try {
        const dataUrl = await inlineCodexReference(url);
        return { imageBase64: dataUrl.split(',')[1], imageBlob: result.imageBlob, mimeType: result.imageBlob.type };
    } finally { URL.revokeObjectURL(url); }
}
